import React, { useState, useMemo, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  Grid,
  Pagination,
  useMediaQuery,
  useTheme,
  Stack,
  Chip,
  TextField
} from '@mui/material';
import { motion } from 'framer-motion';
import MediaPreview from '../utils/MediaPreview';
import { useNavigate } from 'react-router-dom';
import LoadingComponent from '../utils/LoadingComponent';
import { ContactContext } from './ContactContext';
import { showSwalNotification } from '../utils/SwalResponse';
import { AuthContext } from '../auth/AuthContext';




const Contacts = () => {
  const { contacts, loading, contactHandled } = useContext(ContactContext)
  const {currentUser, authToken} = useContext(AuthContext)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const itemsPerPage = 4;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const navigate = useNavigate()

  const handleMarkAsHandled = async (id) => {
    const response = await contactHandled(id);
    showSwalNotification(response)
  };

  useEffect(() => {
    if (!authToken || !currentUser || currentUser.role !== 'support') {
      navigate(-1);
    }
  }, [authToken, currentUser]);

  const filteredContacts = useMemo(() => {
    if (!search.trim()) return contacts;
    const lower = search.toLowerCase();
    return Array.isArray(contacts) && contacts.filter((c) =>
      [c.name, c.email, c.subject, c.message].some((field) =>
        field.toLowerCase().includes(lower)
      )
    );
  }, [contacts, search]);

  const paginatedContacts = filteredContacts.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const pageCount = Math.ceil(filteredContacts.length / itemsPerPage);

 

  if (!!loading) return <LoadingComponent loadingText={`Loading contacts...`} />;

  return (
    <Box sx={{ px: 3, py: 6, background: '#f1f8f6', minHeight: '100vh' }}>
      
      <Stack direction="row" justifyContent="center" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{ color: '#2e7d32' }}
        >
          Contact Submissions
        </Typography>
        <Button
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{ color: 'red', borderColor: 'red',m:5 }}
        >
          Back
        </Button>
      </Stack>

      <Box sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
        <TextField
          fullWidth
          label="Search by name, email, subject, or message"
          variant="outlined"
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reset to page 1 on new search
          }}
        />
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {paginatedContacts.length === 0 ? 
          <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
          No contacts have been submitted yet.
        </Alert> : 
        Array.isArray(paginatedContacts) && paginatedContacts.map((contact) => (
          <Grid item xs={12} sm={10} key={contact.id}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '90vw' : '70vw',
                  flexWrap: 'wrap',
                  boxShadow: 3,
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'white',
                }}
              >
                {contact.image ? (
                  <MediaPreview src={contact.image} alt="Contact attachment" />
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? '100%' : 160,
                      height: 160,
                      bgcolor: '#c8e6c9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2e7d32',
                      fontWeight: 'bold',
                    }}
                  >
                    No Image
                  </Box>
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    mb={1}
                  >
                    <Typography variant="h6">{contact.subject}</Typography>
                    <Chip
                      label={contact.status}
                      color={contact.status === 'New' ? 'warning' : 'success'}
                      size="small"
                    />
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontStyle: 'italic', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                  >
                    {contact.message}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    <strong>Name:</strong> <span>{contact.name}</span>
                  </Typography>
                  {'  | '}
                  <Typography variant="caption" color="text.secondary">
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    <strong>Received:</strong>{' '}
                    {new Date(contact.timestamp).toLocaleString()}
                  </Typography>

                  {contact.status === 'New' && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleMarkAsHandled(contact.id)}
                      sx={{
                        m: 2,
                        backgroundColor: '#2e7d32',
                        '&:hover': { backgroundColor: '#1b5e20' },
                      }}
                    >
                      Mark as Handled
                    </Button>
                  )}
                </CardContent>
              </Card>

            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      <Stack direction="row" justifyContent="center" mt={5}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Stack>
    </Box>
  );
};

export default Contacts;
